<script setup>
import { onMounted, computed, ref, reactive, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { debounce } from 'shared/utils/index.js';

import OrdersListLayout from './components/OrdersListLayout.vue';
import OrdersTable from './components/OrdersTable.vue';
import OrderEmptyState from './components/OrderEmptyState.vue';
import ColumnConfigModal from './components/ColumnConfigModal.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CustomViewsAPI from 'dashboard/api/customViews';
import OrdersAPI from 'dashboard/api/orders';
import ResizableTextArea from 'shared/components/ResizableTextArea.vue';

const DEFAULT_SORT_FIELD = 'placed_at';
const DEBOUNCE_DELAY = 300;

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const orders = useMapGetter('orders/getOrdersList');
const uiFlags = useMapGetter('orders/getUIFlags');
const meta = useMapGetter('orders/getMeta');
const appliedFilters = useMapGetter('orders/getAppliedOrderFilters');
const appliedFiltersV4 = useMapGetter('orders/getAppliedOrderFiltersV4');
const getInboxById = useMapGetter('inboxes/getInboxById');

const ordersHeaderWrapper = ref(null);

const searchQuery = computed(() => route.query?.search);
const searchValue = ref(searchQuery.value || '');
const pageNumber = computed(() => Number(route.query?.page) || 1);

// Column configuration
const showColumnConfig = ref(false);
const visibleColumns = ref([
  'externalOrderId',
  'shopName', 
  'buyerName',
  'source',
  'status',
  'totalCents',
  'paidAt',
  'placedAt',
  'expectedDeliveryDate',
  'isGift',
  'actions'
]);

const sortState = reactive({
  activeSort: DEFAULT_SORT_FIELD,
  activeOrdering: '-',
});

const isFetchingList = computed(() => uiFlags.value.isFetching);
const currentPage = computed(() => Number(meta.value?.currentPage));
const totalItems = computed(() => meta.value?.count);
const hasAppliedFilters = computed(() => appliedFilters.value.length > 0);

const hasOrders = computed(() => orders.value.length > 0);
const isOrderIndexView = computed(
  () => route.name === 'orders_list' && pageNumber.value === 1
);

const showEmptyStateLayout = computed(() => {
  return (
    !searchQuery.value &&
    !hasOrders.value &&
    isOrderIndexView.value &&
    !hasAppliedFilters.value
  );
});

const showEmptyText = computed(() => {
  return (
    (searchQuery.value || !isOrderIndexView.value || hasAppliedFilters.value) &&
    !hasOrders.value
  );
});

const headerTitle = computed(() => {
  if (searchQuery.value) return t('ORDERS_LAYOUT.HEADER.SEARCH_TITLE');
  if (route.name === 'orders_segments_index') {
    return t('ORDERS_LAYOUT.HEADER.TITLE');
  }
  return t('ORDERS_LAYOUT.HEADER.TITLE');
});

const updatePageParam = (page, search = '') => {
  const query = {
    ...route.query,
    page: page.toString(),
    ...(search ? { search } : {}),
  };
  if (!search) delete query.search;
  delete query.filters;
  router.replace({ query });
};

const buildSortAttr = () => `${sortState.activeOrdering}${sortState.activeSort}`;

const getCommonFetchParams = (page = 1) => ({
  page,
  sortAttr: buildSortAttr(),
});

const fetchOrders = async (page = 1) => {
  await store.dispatch('orders/get', getCommonFetchParams(page));
  updatePageParam(page, '');
};

const searchOrders = debounce(async (value, page = 1) => {
  searchValue.value = value;
  if (!value) {
    updatePageParam(page, '');
    await fetchOrders(page);
    return;
  }
  updatePageParam(page, value);
  await store.dispatch('orders/search', {
    ...getCommonFetchParams(page),
    search: encodeURIComponent(value),
  });
}, DEBOUNCE_DELAY);

const fetchOrdersBasedOnContext = async page => {
  if (isFetchingList.value) return;
  if (searchQuery.value) {
    await searchOrders(searchQuery.value, page);
    return;
  }
  searchValue.value = '';
  if (appliedFilters.value.length > 0) {
    await store.dispatch('orders/filter', {
      payload: appliedFilters.value,
      page,
      sortAttr: buildSortAttr(),
    });
    updatePageParam(page, '');
    return;
  }
  await fetchOrders(page);
};

const handleSort = async ({ sort, order }) => {
  Object.assign(sortState, { activeSort: sort, activeOrdering: order });
  if (searchQuery.value) {
    await searchOrders(searchValue.value, 1);
    return;
  }
  if (appliedFilters.value.length > 0) {
    await store.dispatch('orders/filter', {
      payload: appliedFilters.value,
      page: 1,
      sortAttr: buildSortAttr(),
    });
    updatePageParam(1, '');
    return;
  }
  await fetchOrders(1);
};

const handleApplyFilter = async queryPayload => {
  const filters = queryPayload.payload || [];
  await store.dispatch('orders/setOrderFilters', filters);
  if (searchQuery.value) {
    await store.dispatch('orders/filter', {
      payload: filters,
      search: searchValue.value,
      page: 1,
      sortAttr: buildSortAttr(),
    });
    updatePageParam(1, searchValue.value);
    return;
  }
  await store.dispatch('orders/filter', {
    payload: filters,
    page: 1,
    sortAttr: buildSortAttr(),
  });
  updatePageParam(1, '');
};

const handleClearFilters = async () => {
  await store.dispatch('orders/clearOrderFilters');
  if (searchQuery.value) {
    await searchOrders(searchValue.value, 1);
    return;
  }
  await fetchOrders(1);
};

const openFilter = () => {
  const header = ordersHeaderWrapper?.value;
  header?.onToggleFilters?.();
};

// Helpers for modal formatting
const formatPrice = (cents, currency = 'USD') => {
  if (!cents || Number.isNaN(cents)) return '-';
  return `${(cents / 100).toFixed(2)} ${currency}`;
};
const convertToHumanReadableDate = ts => {
  if (!ts || ts === 0) return '-';
  const date = new Date(ts * 1000);
  return date.toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Takecare modal state
const showTakecare = ref(false);
const selectedOrder = ref(null);
const takecareContent = ref('');
const isSendingTakecare = ref(false);

const onOpenTakecare = order => {
  selectedOrder.value = order;
  const externalId = order?.externalOrderId || order?.external_order_id || '';
  const prefixLink = externalId
    ? `https://www.etsy.com/your/purchases/${externalId}\n\n`
    : '';
  takecareContent.value = prefixLink;
  showTakecare.value = true;
};

// Column configuration methods
const openColumnConfig = () => {
  showColumnConfig.value = true;
};

const onColumnConfigSave = (newColumns) => {
  visibleColumns.value = newColumns;
  // Lưu vào localStorage để persist
  localStorage.setItem('orders_visible_columns', JSON.stringify(newColumns));
  showColumnConfig.value = false;
};

// Load saved column configuration
const loadColumnConfig = () => {
  const saved = localStorage.getItem('orders_visible_columns');
  if (saved) {
    try {
      visibleColumns.value = JSON.parse(saved);
    } catch (e) {
      console.warn('Failed to load saved column configuration:', e);
    }
  }
};

const onSendTakecare = async () => {
  if (!selectedOrder.value || !takecareContent.value.trim()) return;
  try {
    isSendingTakecare.value = true;
    await OrdersAPI.takecareMessage(
      selectedOrder.value.id,
      takecareContent.value.trim()
    );
    showTakecare.value = false;
    selectedOrder.value = null;
    takecareContent.value = '';
    window?.$toast?.success?.(t('ORDERS_LAYOUT.TAKECARE.SENT_SUCCESS'));
  } catch (e) {
    window?.$toast?.error?.(
      e?.message || t('ORDERS_LAYOUT.TAKECARE.SENT_FAILED')
    );
  } finally {
    isSendingTakecare.value = false;
  }
};

const selectedInboxName = computed(() => {
  const inboxId = selectedOrder.value?.inboxId;
  if (!inboxId) return '-';
  const inbox = getInboxById.value(inboxId);
  return (
    inbox?.name ||
    selectedOrder.value?.shopName ||
    selectedOrder.value?.externalShopId ||
    '-'
  );
});

const selectedBuyerLine1 = computed(() =>
  selectedOrder.value?.buyerName || '-'
);
const selectedBuyerLine2 = computed(() =>
  selectedOrder.value?.buyerEmail || ''
);

const selectedOrderId = computed(() => selectedOrder.value?.externalOrderId);
const selectedPlacedAt = computed(() =>
  convertToHumanReadableDate(selectedOrder.value?.placedAt)
);
const selectedTotal = computed(() =>
  formatPrice(
    selectedOrder.value?.totalCents,
    selectedOrder.value?.currencyCode
  )
);
const selectedStatus = computed(() => selectedOrder.value?.status);

watch(searchQuery, value => {
  if (isFetchingList.value) return;
  searchValue.value = value || '';
  if (value === undefined) {
    fetchOrders(1);
  }
});

const fetchSegmentAndApply = async segmentId => {
  const { data } = await CustomViewsAPI.getCustomViewsByFilterType(3);
  const views = data || [];
  const view = views.find(v => String(v.id) === String(segmentId));
  if (view && Array.isArray(view.query)) {
    await store.dispatch('orders/setOrderFilters', view.query);
    await store.dispatch('orders/filter', {
      payload: view.query,
      page: 1,
      sortAttr: buildSortAttr(),
    });
  } else {
    await store.dispatch('orders/clearOrderFilters');
    await fetchOrders(1);
  }
};

watch(
  () => route.name,
  async () => {
    if (route.name === 'orders_segments_index') {
      const segmentId = route.params?.segmentId;
      await fetchSegmentAndApply(segmentId);
    } else if (route.name === 'orders_list') {
      await fetchOrders(1);
    }
  }
);

watch(
  () => route.params?.segmentId,
  async newVal => {
    if (route.name === 'orders_segments_index') {
      await fetchSegmentAndApply(newVal);
    }
  }
);

onMounted(async () => {
  // Load saved column configuration
  loadColumnConfig();
  
  if (route.query.filters) {
    try {
      const filters = JSON.parse(route.query.filters);
      await store.dispatch('orders/setOrderFilters', filters);
    } catch (e) {
      await store.dispatch('orders/clearOrderFilters');
    }
  }
  if (route.name === 'orders_segments_index' && route.params?.segmentId) {
    await fetchSegmentAndApply(route.params.segmentId);
    return;
  }
  await fetchOrdersBasedOnContext(pageNumber.value);
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background"
  >
    <OrdersListLayout
      :search-value="searchValue"
      :header-title="headerTitle"
      :current-page="currentPage"
      :total-items="totalItems"
      :show-pagination-footer="!isFetchingList && hasOrders"
      :active-sort="sortState.activeSort"
      :active-ordering="sortState.activeOrdering"
      :has-applied-filters="hasAppliedFilters"
      :applied-filters="appliedFiltersV4"
      :is-fetching-list="isFetchingList"
      @update:current-page="fetchOrdersBasedOnContext"
      @search="searchOrders"
      @update:sort="handleSort"
      @apply-filter="handleApplyFilter"
      @clear-filters="handleClearFilters"
      @open-filter="openFilter"
      @open-column-config="openColumnConfig"
      ref="ordersHeaderWrapper"
    >
      <div
        v-if="isFetchingList"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>

      <template v-else>
        <OrderEmptyState
          v-if="showEmptyStateLayout"
          class="pt-14"
          :title="t('ORDERS_LAYOUT.EMPTY_STATE.TITLE')"
          :subtitle="t('ORDERS_LAYOUT.EMPTY_STATE.SUBTITLE')"
          :button-label="t('ORDERS_LAYOUT.EMPTY_STATE.BUTTON_LABEL')"
        />

        <div
          v-else-if="showEmptyText"
          class="flex items-center justify-center py-10"
        >
          <span class="text-base text-n-slate-11">
            {{
              searchQuery || hasAppliedFilters
                ? t('ORDERS_LAYOUT.EMPTY_STATE.SEARCH_EMPTY_STATE_TITLE')
                : t('ORDERS_LAYOUT.EMPTY_STATE.LIST_EMPTY_STATE_TITLE')
            }}
          </span>
        </div>

        <OrdersTable 
          v-else 
          :orders="orders" 
          :visible-columns="visibleColumns"
          @takecare="onOpenTakecare" 
        />
      </template>
    </OrdersListLayout>

    <!-- Takecare Modal -->
    <woot-modal
      v-model:show="showTakecare"
      :on-close="() => (showTakecare = false)"
    >
      <woot-modal-header :header-title="t('ORDERS_LAYOUT.TAKECARE.TITLE')" />
      <div class="p-6 space-y-5">
        <!-- Info panel -->
        <div
          class="bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <div class="flex items-start gap-3">
            <fluent-icon
              icon="building-shop"
              size="16"
              class="mt-0.5 text-slate-500"
            />
            <div>
              <div class="text-xs text-slate-500">{{ t('TABLE.SHOP') }}</div>
              <div
                class="text-sm font-medium text-slate-900 dark:text-slate-100"
              >
                {{ selectedInboxName }}
              </div>
            </div>
          </div>
          <div class="flex items-start gap-3">
            <fluent-icon
              icon="person"
              size="16"
              class="mt-0.5 text-slate-500"
            />
            <div>
              <div class="text-xs text-slate-500">{{ t('TABLE.BUYER') }}</div>
              <div
                class="text-sm font-medium text-slate-900 dark:text-slate-100"
              >
                {{ selectedBuyerLine1 }}
              </div>
              <div v-if="selectedBuyerLine2" class="text-xs text-slate-500">
                {{ selectedBuyerLine2 }}
              </div>
            </div>
          </div>
          <div class="flex items-start gap-3">
            <fluent-icon
              icon="number-symbol"
              size="16"
              class="mt-0.5 text-slate-500"
            />
            <div>
              <div class="text-xs text-slate-500">
                {{ t('TABLE.ORDER_ID') }}
              </div>
              <div
                class="text-sm font-medium text-slate-900 dark:text-slate-100"
              >
                #{{ selectedOrderId }}
              </div>
            </div>
          </div>
          <div class="flex items-start gap-3">
            <fluent-icon
              icon="calendar"
              size="16"
              class="mt-0.5 text-slate-500"
            />
            <div>
              <div class="text-xs text-slate-500">
                {{ t('TABLE.PLACED_DATE') }}
              </div>
              <div
                class="text-sm font-medium text-slate-900 dark:text-slate-100"
              >
                {{ selectedPlacedAt }}
              </div>
            </div>
          </div>
          <div class="flex items-start gap-3">
            <fluent-icon icon="money" size="16" class="mt-0.5 text-slate-500" />
            <div>
              <div class="text-xs text-slate-500">{{ t('TOTAL') }}</div>
              <div
                class="text-sm font-medium text-slate-900 dark:text-slate-100"
              >
                {{ selectedTotal }}
              </div>
            </div>
          </div>
          <div class="flex items-start gap-3">
            <fluent-icon icon="tag" size="16" class="mt-0.5 text-slate-500" />
            <div>
              <div class="text-xs text-slate-500">{{ t('STATUS_TITLE') }}</div>
              <div
                class="text-sm font-medium text-slate-900 dark:text-slate-100 capitalize"
              >
                {{ selectedStatus }}
              </div>
            </div>
          </div>
        </div>

        <!-- Composer -->
        <div class="space-y-2">
          <label class="text-sm text-slate-600 dark:text-slate-300">
            {{ t('ORDERS_LAYOUT.TAKECARE.MESSAGE_LABEL') }}
          </label>
          <ResizableTextArea
            v-model="takecareContent"
            :placeholder="t('ORDERS_LAYOUT.TAKECARE.MESSAGE_PLACEHOLDER')"
            :rows="6"
            :min-height="8"
          />
        </div>
      </div>
      <div class="px-6 py-4 flex justify-end gap-2">
        <woot-button variant="clear" @click="showTakecare = false">
          {{ t('CANCEL') }}
        </woot-button>
        <woot-button
          :loading="isSendingTakecare"
          :disabled="!takecareContent.trim()"
          @click="onSendTakecare"
        >
          {{ t('SEND') }}
        </woot-button>
      </div>
    </woot-modal>

    <!-- Column Configuration Modal -->
    <ColumnConfigModal
      v-model:visible="showColumnConfig"
      :selected-columns="visibleColumns"
      @save="onColumnConfigSave"
    />
  </div>
</template>
